const formatCurrency = (amount) => (amount === 0 ? '$0' : `$${Math.trunc(amount).toLocaleString()}`);

const getVmCount = (c) => (typeof c === 'number' ? c : (c?.vms ?? 0));
const getCpuCount = (c) => (typeof c === 'number' ? c : (c?.vCPUs ?? 0));

export const calcModernizationSavingsFromApi = (osPricingData, summaryData, yearIndex = 0) => {
    const yearKey = `year_${yearIndex + 1}`;

    const windowsPricing = {};
    const linuxPricing = {};

    // Go through response from modernization-savings API and create object for Windows and Linux pricing lookup
    (osPricingData?.os ?? []).forEach((item) => {
        if (item.windows) {
            Object.entries(item.windows).forEach(([version, editions]) => {
                windowsPricing[version] = { ...(windowsPricing[version] ?? {}), ...editions };
            });
        }
        if (item.linux) {
            Object.entries(item.linux).forEach(([distro, versions]) => {
                if (!linuxPricing[distro]) {
                    linuxPricing[distro] = {};
                }
                Object.entries(versions).forEach(([version, pricing]) => {
                    linuxPricing[distro][version] = pricing;
                });
            });
        }
    });

    const hasWindowsPricing = Object.keys(windowsPricing).length > 0;

    const windowsVersions = summaryData?.count?.windows?.versions ?? {};
    let windowsCount = 0;
    let windowsSavings = 0;
    let unsupportedCount = 0;

    // Create lookup for Windows savings by version/edition, and add up supported vs unsupported machines.
    // Any machine with a version/edition that doesn't have pricing is unsupported.
    Object.entries(windowsVersions).forEach(([version, versionData]) => {
        const editions = versionData.editions ?? {};
        if (Object.keys(editions).length === 0) {
            if (versionData.total) {
                unsupportedCount += versionData.total;
            }
            return;
        }
        Object.entries(editions).forEach(([edition, count]) => {
            if (!count) {
                return;
            }
            const pricing = windowsPricing[version]?.[edition];
            if (pricing) {
                windowsCount += count;
                windowsSavings += count * Number(pricing[yearKey] ?? 0);
            } else {
                unsupportedCount += count;
            }
        });
    });

    // Any Windows machines not captured in version/edition data are Unsupported
    const windowsTotal = summaryData?.count?.windows?.total ?? 0;
    const unaccountedWindows = windowsTotal - windowsCount - unsupportedCount;
    if (unaccountedWindows > 0) {
        unsupportedCount += unaccountedWindows;
    }

    // Create lookup for Linux savings by vendor/version.
    // costType "CPU" uses vCPUs for savings; "OS" uses VM count.
    const linuxVendors = summaryData?.count?.linux?.vendors ?? {};
    const hasLinuxPricing = Object.keys(linuxPricing).length > 0;
    let linuxCount = 0;
    let linuxSavings = 0;

    Object.entries(linuxVendors).forEach(([vendor, vendorData]) => {
        linuxCount += vendorData.total ?? 0;

        if (!hasLinuxPricing) {
            return;
        }

        const versionEntries = Object.entries(vendorData.versions ?? {}).filter(
            ([, c]) => getVmCount(c) > 0 || getCpuCount(c) > 0,
        );
        if (versionEntries.length === 0) {
            // No version breakdown — apply wildcard to entire vendor total
            const pricing = linuxPricing[vendor]?.['*'];
            if (pricing && vendorData.total) {
                linuxSavings += vendorData.total * Number(pricing[yearKey] ?? 0);
            }
        } else {
            versionEntries.forEach(([version, countData]) => {
                const pricing = linuxPricing[vendor]?.[version] ?? linuxPricing[vendor]?.['*'];
                if (pricing) {
                    const count = pricing.costType === 'CPU' ? getCpuCount(countData) : getVmCount(countData);
                    linuxSavings += count * Number(pricing[yearKey] ?? 0);
                }
            });
            // Machines with no version info within a vendor — apply wildcard if available
            const versionedVms = versionEntries.reduce((sum, [, c]) => sum + getVmCount(c), 0);
            const unversionedCount = (vendorData.total ?? 0) - versionedVms;
            if (unversionedCount > 0) {
                const pricing = linuxPricing[vendor]?.['*'];
                if (pricing) {
                    linuxSavings += unversionedCount * Number(pricing[yearKey] ?? 0);
                }
            }
        }
    });

    // Unvendored Linux machines go to Unsupported
    const unvendoredLinux = (summaryData?.count?.linux?.total ?? 0) - linuxCount;
    if (unvendoredLinux > 0) {
        unsupportedCount += unvendoredLinux;
    }

    return [
        {
            mainLabel: 'Windows',
            mainValue: windowsCount,
            secondaryLabel: 'Savings',
            secondaryValue: hasWindowsPricing ? formatCurrency(windowsSavings) : 'N/A',
        },
        {
            mainLabel: 'Linux',
            mainValue: linuxCount,
            secondaryLabel: 'Savings',
            secondaryValue: hasLinuxPricing ? formatCurrency(linuxSavings) : 'N/A',
        },
        {
            mainLabel: 'Others',
            mainValue: unsupportedCount,
            secondaryLabel: 'Savings',
            secondaryValue: 'N/A',
        },
    ];
};
